import { authResponseSchema, hasAccessToken, isApiError, unwrap, type InvitationPublicOut, type MeOut } from "@sagot/api-client";
import { CONSOLE_PATHS, LEGAL_LINKS, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PRODUCT_NAME, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input, Skeleton, toast } from "@sagot/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { queryKeys } from "../lib/query-keys";
import { zodResolver } from "../lib/zod-resolver";
import { useApi, useClient, useSession } from "../session/session-provider";
import { useMe } from "../session/use-me";
import { AuthDivider } from "./components/auth-divider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { OAuthButton } from "./components/oauth-buttons";
import { PasswordInput } from "./components/password-input";
import { PasswordStrength } from "./components/password-strength";
import { InviteSummary } from "./invite-summary";

function useAcceptSchema() {
  const { t } = useTranslation();
  return z.object({
    name: z.string().trim().min(1, t("auth.common.nameRequired")).max(NAME_MAX_LENGTH),
    title: z.string().trim().max(200),
    password: z.string().min(PASSWORD_MIN_LENGTH, t("auth.password.tooShort")).max(PASSWORD_MAX_LENGTH),
    consent: z.literal(true, { errorMap: () => ({ message: t("auth.register.consentRequired") }) }),
  });
}
type AcceptValues = z.infer<ReturnType<typeof useAcceptSchema>>;

/** After acceptance: adopt the new session (new account) and open the joined workspace. */
function useFinishAccept() {
  const client = useClient();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return async (workspaceId: string, auth: unknown, workspaceName: string) => {
    const parsed = authResponseSchema.safeParse(auth);
    if (parsed.success && hasAccessToken(parsed.data)) client.session.setSession(parsed.data);
    const me: MeOut = await queryClient.query({ queryKey: queryKeys.me, queryFn: () => unwrap(api.GET("/v1/me")), staleTime: 0 });
    const slug = me.workspaces.find((w) => w.id === workspaceId)?.slug;
    toast.success(t("auth.invite.joined", { workspace: workspaceName }));
    await navigate(slug ? consoleRoutes.dashboard(slug) : "/", { replace: true });
  };
}

function NewAccountForm({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const finish = useFinishAccept();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, control, formState } = useForm<AcceptValues>({ resolver: zodResolver(useAcceptSchema()), defaultValues: { name: "", title: "", password: "" } });
  const password = useWatch({ control, name: "password" });
  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const result = await unwrap(api.POST("/v1/invitations/{token}/accept", { params: { path: { token } }, body: { name: v.name, title: v.title || null, password: v.password, consent: v.consent } }));
      await finish(result.workspace_id, result.auth, invitation.workspace_name);
    } catch (caught) {
      setError(message(caught));
    }
  });
  return (
    <>
      <OAuthButton provider="google" intent="invite" invite={token} label={t("auth.common.continueGoogle")} className="w-full" />
      <AuthDivider label={t("auth.invite.orSetPassword")} />
      {error ? (
        <FormAlert
          title={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <form noValidate onSubmit={(event) => void onSubmit(event)}>
        <Field className="a-fld" label={t("auth.register.fullName")} error={formState.errors.name?.message}>
          <Input autoComplete="name" {...register("name")} />
        </Field>
        <Field
          className="a-fld"
          label={
            <>
              {t("auth.invite.jobTitle")} <span className="a-opt">{t("auth.invite.optional")}</span>
            </>
          }
        >
          <Input autoComplete="organization-title" {...register("title")} />
        </Field>
        <Field className="a-fld" label={t("auth.common.password")} error={formState.errors.password?.message}>
          <PasswordInput placeholder={t("auth.password.placeholder")} autoComplete="new-password" {...register("password")} />
          <PasswordStrength value={password} />
        </Field>
        <div className="a-row a-row-col">
          <label className="a-chk">
            <input type="checkbox" className="checkbox" aria-invalid={formState.errors.consent ? true : undefined} {...register("consent")} />
            <span>
              {t("auth.invite.consentLead")}{" "}
              <a href={LEGAL_LINKS.terms} target="_blank" rel="noreferrer" className="a-link">
                {t("auth.common.terms")}
              </a>{" "}
              {t("auth.invite.consentTail")}
            </span>
          </label>
          {formState.errors.consent ? (
            <p role="alert" className="a-err-line">
              {formState.errors.consent.message}
            </p>
          ) : null}
        </div>
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
          {t("auth.invite.submit")}
        </Button>
      </form>
    </>
  );
}

const sameAddress = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function SignedInAccept({ token, invitation, email }: { token: string; invitation: InvitationPublicOut; email: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const client = useClient();
  const message = useErrorMessage();
  const finish = useFinishAccept();
  const [busy, setBusy] = useState(false);
  if (!sameAddress(email, invitation.email)) {
    return (
      <FormAlert tone="warning" title={t("auth.invite.mismatchTitle")}>
        {t("auth.invite.mismatchBody", { current: email, invited: invitation.email })}{" "}
        <button type="button" className="a-link" onClick={() => void client.session.logout()}>
          {t("auth.invite.signOut")}
        </button>
      </FormAlert>
    );
  }
  const accept = async () => {
    setBusy(true);
    try {
      const result = await unwrap(api.POST("/v1/invitations/{token}/accept", { params: { path: { token } }, body: { consent: true } }));
      await finish(result.workspace_id, result.auth, invitation.workspace_name);
    } catch (caught) {
      toast.error(message(caught));
      setBusy(false);
    }
  };
  return (
    <Button size="lg" className="w-full" loading={busy} onClick={() => void accept()}>
      {t("auth.invite.acceptSignedIn")}
    </Button>
  );
}

function DeclineLink({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const decline = async () => {
    try {
      await unwrap(api.POST("/v1/invitations/{token}/decline", { params: { path: { token } } }));
      toast.info(invitation.inviter_name ? t("auth.invite.declined", { inviter: invitation.inviter_name }) : t("auth.invite.declinedNoInviter"));
      await navigate(CONSOLE_PATHS.login, { replace: true });
    } catch (caught) {
      toast.error(message(caught));
    }
  };
  return (
    <p className="a-foot">
      {t("auth.invite.notYou")}{" "}
      <button type="button" className="a-link" onClick={() => void decline()}>
        {t("auth.invite.decline")}
      </button>
    </p>
  );
}

/** Same blocks as the loaded page (title, line, invite card, fields, button), so nothing jumps when the invitation arrives. */
function InviteSkeleton() {
  return (
    <div aria-busy="true" data-testid="invite-loading">
      <Skeleton className="mx-auto mb-3 mt-1.5 h-10 w-[300px] max-w-full" />
      <Skeleton className="mx-auto mb-7 h-4 w-[240px] max-w-full" />
      <Skeleton className="mb-6 h-[220px] w-full rounded-[14px]" />
      <Skeleton className="mb-4 h-[50px] w-full rounded-full" />
      <Skeleton className="mb-4 h-[50px] w-full rounded-full" />
      <Skeleton className="mb-4 h-[50px] w-full rounded-full" />
      <Skeleton className="h-[50px] w-full rounded-full" />
    </div>
  );
}

function InviteBody({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const session = useSession();
  const me = useMe();
  const signedIn = session.status === "authenticated";
  const account = signedIn ? me.data?.user.email : undefined;
  const accepting = account !== undefined && sameAddress(account, invitation.email);
  return (
    <section aria-labelledby="invite-title">
      <h1 id="invite-title" className="a-title">
        {t("auth.invite.setupHeading")}
      </h1>
      <p className="a-sub">
        {accepting ? (
          t("auth.invite.signedInAs", { email: account })
        ) : (
          <>
            {t("auth.invite.joiningAs")} <strong>{invitation.email}</strong>.
          </>
        )}
      </p>
      <InviteSummary invitation={invitation} />
      {account !== undefined ? <SignedInAccept token={token} invitation={invitation} email={account} /> : signedIn && me.isPending ? <Skeleton className="h-[50px] w-full rounded-full" /> : <NewAccountForm token={token} invitation={invitation} />}
      <DeclineLink token={token} invitation={invitation} />
    </section>
  );
}

/** The link is no longer usable (expired, revoked, already accepted, or never existed): a designed state, not a bare message. */
function InviteUnavailable() {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="invite-unavailable-title" data-testid="invite-unavailable">
      <span className="a-state" data-tone="warn">
        <Icon name="mail" />
      </span>
      <h1 id="invite-unavailable-title" className="a-title">
        {t("auth.invite.invalidTitle")}
      </h1>
      <p className="a-sub">{t("auth.invite.invalidBody")}</p>
      <Button asChild size="lg" className="w-full">
        <AppLink href={CONSOLE_PATHS.login}>{t("auth.invite.goToSignIn")}</AppLink>
      </Button>
    </section>
  );
}

/** The invitation could not be fetched (network, timeout, server error): the link may still be good, so offer a retry. */
function InviteLoadFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="invite-failed-title" data-testid="invite-load-failed">
      <span className="a-state" data-tone="warn">
        <Icon name="alert-triangle" />
      </span>
      <h1 id="invite-failed-title" className="a-title">
        {t("auth.invite.loadFailedTitle")}
      </h1>
      <p className="a-sub">{t("auth.invite.loadFailedBody")}</p>
      <Button size="lg" className="w-full" onClick={onRetry}>
        <Icon name="refresh-cw" />
        {t("auth.invite.retry")}
      </Button>
      <p className="a-foot">
        <AppLink href={CONSOLE_PATHS.login} className="a-link">
          {t("auth.invite.goToSignIn")}
        </AppLink>
      </p>
    </section>
  );
}

const isTransient = (error: unknown) => isApiError(error) && (error.kind === "network" || error.kind === "timeout" || error.status >= 500);

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const api = useApi();
  const { token = "" } = useParams();
  const invitation = useQuery({
    queryKey: queryKeys.invitation(token),
    queryFn: () => unwrap(api.GET("/v1/invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });
  const next = `${CONSOLE_PATHS.login}?next=${encodeURIComponent(consoleRoutes.acceptInvite(token))}`;
  return (
    <AuthLayout
      title={t("auth.invite.title", { product: PRODUCT_NAME })}
      prompt={{ text: t("auth.common.alreadyHaveAccount"), label: t("auth.invite.signInToAccept"), href: next }}
      width="md"
      align="start"
    >
      {invitation.isPending ? (
        <InviteSkeleton />
      ) : invitation.isError ? (
        isTransient(invitation.error) ? (
          <InviteLoadFailed
            onRetry={() => {
              void invitation.refetch();
            }}
          />
        ) : (
          <InviteUnavailable />
        )
      ) : (
        <InviteBody token={token} invitation={invitation.data} />
      )}
    </AuthLayout>
  );
}
